"use client";

import { c as _c } from "react/compiler-runtime";
import { useParams, useRouter } from "next/navigation";
import { ChevronRight } from "lucide-react";
import Box from "@/components/Box";
import XStack from "@/components/XStack";
import YStack from "@/components/YStack";
import SizeableText from "@/components/SizeableText";
import Button from "@/components/Button";
import Badge from "@/components/Badge";
import { agentsById } from "../agents";
import { PhoneMockup } from "../store-components/PhoneMockup";
import { AgentIcon } from "../store-components/AgentIcon";
import styles from "./page.module.css";
export default function AgentDetailPage() {
  const $ = _c(55);
  const params = useParams();
  const router = useRouter();
  const agentId = params.agentId as string;
  const agent = agentsById[agentId];
  if (!agent) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <YStack className={styles.page} padding={8}><SizeableText size="large">Agent not found</SizeableText></YStack>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  const firstConversation = agent.conversations[0];
  let t0;
  if ($[1] !== router) {
    t0 = <span className={styles.breadcrumbLink} onClick={() => router.push("/")}>Message Agents</span>;
    $[1] = router;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ChevronRight size={16} className={styles.breadcrumbChevron} />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== agent.name) {
    t2 = <span className={styles.breadcrumbText}>{agent.name}</span>;
    $[4] = agent.name;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t0 || $[7] !== t2) {
    t3 = <Box className={styles.header}><XStack maxWidth={1200} marginHorizontal="auto" alignItems="center" gap={2}>{t0}{t1}{t2}</XStack></Box>;
    $[6] = t0;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== agent.iconBg || $[10] !== agent.id) {
    t4 = agent.id === "passage-money" ? <Box className={styles.agentIcon} style={{
      background: agent.iconBg
    }}><AgentIcon agentId={agent.id} size={40} /></Box> : <Box style={{
      borderRadius: 12,
      overflow: "hidden"
    }}><AgentIcon agentId={agent.id} size={56} /></Box>;
    $[9] = agent.iconBg;
    $[10] = agent.id;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== agent.name) {
    t5 = <SizeableText size="xlarge" fontWeight="strong" className={styles.text}>{agent.name}</SizeableText>;
    $[12] = agent.name;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== agent.isOfficial) {
    t6 = !agent.isOfficial && <Badge variant="warning" size="small">Community</Badge>;
    $[14] = agent.isOfficial;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <XStack alignItems="center" gap={2}>{t5}{t6}</XStack>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== agent.tagline) {
    t8 = <SizeableText size="medium" color="secondary" className={styles.text}>{agent.tagline}</SizeableText>;
    $[19] = agent.tagline;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = <YStack gap={0}>{t7}{t8}</YStack>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t4 || $[25] !== t9) {
    t10 = <XStack alignItems="center" gap={4}>{t4}{t9}</XStack>;
    $[24] = t4;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== agent.initialMessage || $[28] !== agent.smsPhone) {
    t11 = <Button label="Start Talking" color="primary" size="large" className={styles.headerButton} onClick={() => {
      window.location.href = `sms:${agent.smsPhone}?body=${encodeURIComponent(agent.initialMessage)}`;
    }} />;
    $[27] = agent.initialMessage;
    $[28] = agent.smsPhone;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t10 || $[31] !== t11) {
    t12 = <Box maxWidth={1200} marginHorizontal="auto" paddingHorizontal={8} marginTop={8}><Box className={styles.agentHeader}>{t10}{t11}</Box></Box>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== agent.heroBg) {
    t13 = <Box className={styles.showcaseGradient} style={{
      background: agent.heroBg
    }} />;
    $[33] = agent.heroBg;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== agent.name || $[36] !== firstConversation.agentDetail || $[37] !== firstConversation.agentResponse || $[38] !== firstConversation.userMessage) {
    t14 = <XStack justifyContent="center" padding={8} className={styles.showcaseContent}><PhoneMockup agentName={agent.name} userMessage={firstConversation.userMessage} agentResponse={firstConversation.agentResponse} agentDetail={firstConversation.agentDetail} /></XStack>;
    $[35] = agent.name;
    $[36] = firstConversation.agentDetail;
    $[37] = firstConversation.agentResponse;
    $[38] = firstConversation.userMessage;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t13 || $[41] !== t14) {
    t15 = <Box maxWidth={1200} marginHorizontal="auto" paddingHorizontal={8} marginTop={8}><Box className={styles.showcaseContainer}>{t13}{t14}</Box></Box>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== agent.description) {
    t16 = <Box maxWidth={1200} marginHorizontal="auto" paddingHorizontal={8} marginTop={8}><SizeableText size="medium" className={styles.text}>{agent.description}</SizeableText></Box>;
    $[43] = agent.description;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SizeableText size="large" fontWeight="strong" className={styles.text}>Information</SizeableText>;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <SizeableText size="medium" color="secondary" className={styles.text}>Category</SizeableText>;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  const t19 = agent.category === "finance" ? "Finance" : agent.category === "food" ? "Food & Dining" : "Services";
  let t20;
  if ($[47] !== t19) {
    t20 = <Box maxWidth={1200} marginHorizontal="auto" paddingHorizontal={8} marginTop={8} paddingBottom={12}><YStack gap={4}>{t17}<Box className={styles.infoCard}><XStack justifyContent="space-between" padding={4}>{t18}<SizeableText size="medium" className={styles.text}>{t19}</SizeableText></XStack></Box></YStack></Box>;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t12 || $[50] !== t15 || $[51] !== t16 || $[52] !== t20 || $[53] !== t3) {
    t21 = <YStack className={styles.page}>{t3}{t12}{t15}{t16}{t20}</YStack>;
    $[49] = t12;
    $[50] = t15;
    $[51] = t16;
    $[52] = t20;
    $[53] = t3;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
}